import API from "./api";

const KEY = "ebek_offline_visits_v1";
export const OFFLINE_VISITS_STORAGE_KEY = KEY;
const DB_NAME = "ebek-one-offline";
const STORE = "visit-photos";

function currentUser() {
  try { return JSON.parse(localStorage.getItem("crm_user") || "null"); }
  catch { return null; }
}

function allQueuedVisits() {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); }
  catch { return []; }
}

export const queuedVisits = () => {
  const userId = Number(currentUser()?.id || 0);
  if (!userId) return [];
  // Rows created by older builds had no owner. Claim those rows for the user
  // currently signed in on this device, then keep every future queue isolated.
  let changed = false;
  const all = allQueuedVisits().map(row => {
    if (row.owner_user_id) return row;
    changed = true;
    return { ...row, owner_user_id: userId };
  });
  if (changed) localStorage.setItem(KEY, JSON.stringify(all));
  return all.filter(row => Number(row.owner_user_id) === userId);
};

function persistQueue(rows) {
  const userId = Number(currentUser()?.id || 0);
  const otherUsers = allQueuedVisits().filter(row => Number(row.owner_user_id) !== userId);
  localStorage.setItem(KEY, JSON.stringify([...otherUsers, ...rows.map(row => ({ ...row, owner_user_id: userId }))]));
  window.dispatchEvent(new Event("offline-visits-changed"));
}

function mediaDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function cloneForStorage(file) {
  const buffer = await file.arrayBuffer();
  const type = file.type || "image/jpeg";
  return {
    blob: new Blob([buffer], { type }),
    name: file.name || "offline-visit.jpg",
    type,
  };
}

function storedToFile(value) {
  if (!value) return null;
  if (value instanceof File) return value;
  if (value instanceof Blob) return new File([value], "offline-visit.jpg", { type: value.type || "image/jpeg" });
  if (value.blob instanceof Blob) {
    return new File([value.blob], value.name || "offline-visit.jpg", { type: value.type || value.blob.type || "image/jpeg" });
  }
  return null;
}

async function putPhoto(id, file) {
  if (!file) return;
  const stored = await cloneForStorage(file);
  const db = await mediaDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(stored, id);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

async function getPhoto(id) {
  const db = await mediaDb();
  const value = await new Promise((resolve, reject) => {
    const request = db.transaction(STORE).objectStore(STORE).get(id);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return storedToFile(value);
}

async function removePhoto(id) {
  const db = await mediaDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function stashDraftPhoto(draftKey, file) {
  if (!draftKey || !file) return;
  await putPhoto(draftKey, file);
}

export async function loadDraftPhoto(draftKey) {
  if (!draftKey) return null;
  return getPhoto(draftKey);
}

export async function clearDraftPhoto(draftKey) {
  if (!draftKey) return;
  try { await removePhoto(draftKey); } catch { /* draft photo is best-effort */ }
}

export async function prepareVisitPhoto(file) {
  if (!file || file.size <= 2 * 1024 * 1024) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const maxSide = 1600;
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d", { alpha: false });
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(value => value ? resolve(value) : reject(new Error("Photo resize failed")), "image/jpeg", 0.82);
    });
    return new File([blob], `${String(file.name || "visit-photo").replace(/\.[^.]+$/, "")}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    // Some older browsers cannot decode every camera format. The backend has
    // a safe upper limit and malware scan, so retain the original in that case.
    return file;
  }
}

export async function uploadPhoto(file) {
  const prepared = await prepareVisitPhoto(file);
  const form = new FormData();
  const filename = prepared.name || "offline-visit.jpg";
  form.append("image", prepared, filename);
  const token = localStorage.getItem("crm_token") || localStorage.getItem("token");
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:4001/api";
  const response = await fetch(`${baseUrl.replace(/\/api$/, "")}/api/uploads/photo`, {
    method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form,
  });
  const contentType = response.headers.get("content-type") || "";
  const json = contentType.includes("application/json") ? await response.json() : {};
  if (!response.ok) {
    const reason = json.error || (response.status === 413 ? "Photo is too large to upload" : `Photo upload failed (${response.status})`);
    throw new Error(reason);
  }
  if (!json.success || !json.url) throw new Error(json.error || "Offline photo upload failed");
  return json.url;
}

export async function queueVisit(payload, photoFile = null) {
  const owner = currentUser();
  if (!owner?.id) throw new Error("Sign in again before saving this visit offline");
  const id = payload.client_sync_id || `visit-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  if (photoFile) await putPhoto(id, photoFile);
  const rows = queuedVisits();
  rows.push({
    ...payload,
    client_sync_id: id,
    owner_user_id: Number(owner.id),
    owner_email: owner.email || null,
    has_offline_photo: !!photoFile,
    queued_at: payload.queued_at || new Date().toISOString(),
    attempts: 0,
    last_error: null,
  });
  persistQueue(rows);
  return rows.length;
}

function updateQueuedRow(clientSyncId, patch) {
  persistQueue(queuedVisits().map((row) => (
    row.client_sync_id === clientSyncId ? { ...row, ...patch } : row
  )));
}

function dropQueuedRow(clientSyncId) {
  persistQueue(queuedVisits().filter((row) => row.client_sync_id !== clientSyncId));
}

let activeFlush = null;

async function runFlushVisits() {
  if (!navigator.onLine) return 0;
  const rows = queuedVisits();
  let done = 0;
  for (const queued of rows) {
    // Do not hammer the API every time the PWA wakes or changes screen after a
    // known failure. The Sync Centre Retry button clears last_error and always
    // permits an immediate user-requested attempt.
    const lastAttempt = queued.last_attempt_at ? new Date(queued.last_attempt_at).getTime() : 0;
    if (queued.last_error && lastAttempt && Date.now() - lastAttempt < 15 * 60 * 1000) continue;
    try {
      const row = { ...queued };
      delete row.has_offline_photo;
      delete row.attempts;
      delete row.last_error;
      delete row.last_attempt_at;
      delete row.owner_user_id;
      delete row.owner_email;
      if (queued.has_offline_photo && !row.photo_url) {
        const photo = await getPhoto(queued.client_sync_id);
        if (!photo) throw new Error("Queued photo is unavailable");
        row.photo_url = await uploadPhoto(photo);
        updateQueuedRow(queued.client_sync_id, { photo_url: row.photo_url });
      }
      await API.post("/visits", row);
      try {
        if (queued.has_offline_photo) await removePhoto(queued.client_sync_id);
      } catch { /* visit is already on the server */ }
      dropQueuedRow(queued.client_sync_id);
      done += 1;
    } catch (error) {
      updateQueuedRow(queued.client_sync_id, {
        attempts: Number(queued.attempts || 0) + 1,
        last_error: String(error?.response?.data?.error || error?.message || "Sync failed").slice(0, 160),
        last_attempt_at: new Date().toISOString(),
      });
    }
  }
  return done;
}

export function flushVisits() {
  if (activeFlush) return activeFlush;
  activeFlush = runFlushVisits().finally(() => { activeFlush = null; });
  return activeFlush;
}

export function retryVisit(clientSyncId) {
  updateQueuedRow(clientSyncId, { last_error: null });
  return flushVisits();
}

export async function discardVisit(clientSyncId) {
  const target = queuedVisits().find((row) => row.client_sync_id === clientSyncId);
  if (target?.has_offline_photo) await removePhoto(clientSyncId);
  dropQueuedRow(clientSyncId);
}
